import React, { useEffect, useRef, useState } from "react"
import { makeStyles } from "@material-ui/core/styles"
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps"
import worldData from "world-atlas/countries-110m.json"

// Single source of truth for the patient countries.
//   id   — numeric ISO 3166-1 code (string — world-atlas stores them as strings)
//   code — ISO 3166-1 alpha-2 (drives the /flags/<CODE>.svg lookup)
//   en/nl — display name per locale
// Insertion order drives both the map reveal sequence and the list order.
export const PATIENT_COUNTRIES = [
  { id: "528", code: "NL", en: "Netherlands", nl: "Nederland" }, // home — lit first
  { id: "056", code: "BE", en: "Belgium", nl: "België" },
  { id: "276", code: "DE", en: "Germany", nl: "Duitsland" },
  { id: "250", code: "FR", en: "France", nl: "Frankrijk" },
  { id: "826", code: "GB", en: "United Kingdom", nl: "Verenigd Koninkrijk" },
  { id: "372", code: "IE", en: "Ireland", nl: "Ierland" },
  { id: "442", code: "LU", en: "Luxembourg", nl: "Luxemburg" },
  { id: "756", code: "CH", en: "Switzerland", nl: "Zwitserland" },
  { id: "040", code: "AT", en: "Austria", nl: "Oostenrijk" },
  { id: "208", code: "DK", en: "Denmark", nl: "Denemarken" },
  { id: "578", code: "NO", en: "Norway", nl: "Noorwegen" },
  { id: "752", code: "SE", en: "Sweden", nl: "Zweden" },
  { id: "246", code: "FI", en: "Finland", nl: "Finland" },
  { id: "352", code: "IS", en: "Iceland", nl: "IJsland" },
  { id: "724", code: "ES", en: "Spain", nl: "Spanje" },
  { id: "620", code: "PT", en: "Portugal", nl: "Portugal" },
  { id: "380", code: "IT", en: "Italy", nl: "Italië" },
  { id: "300", code: "GR", en: "Greece", nl: "Griekenland" },
  { id: "470", code: "MT", en: "Malta", nl: "Malta" },
  { id: "642", code: "RO", en: "Romania", nl: "Roemenië" },
  { id: "203", code: "CZ", en: "Czech Republic", nl: "Tsjechië" },
  { id: "703", code: "SK", en: "Slovakia", nl: "Slowakije" },
  { id: "616", code: "PL", en: "Poland", nl: "Polen" },
  { id: "348", code: "HU", en: "Hungary", nl: "Hongarije" },
  { id: "688", code: "RS", en: "Serbia", nl: "Servië" },
  { id: "008", code: "AL", en: "Albania", nl: "Albanië" },
  { id: "807", code: "MK", en: "North Macedonia", nl: "Noord-Macedonië" },
  { id: "643", code: "RU", en: "Russia", nl: "Rusland" },
  { id: "792", code: "TR", en: "Turkey", nl: "Turkije" },
  { id: "504", code: "MA", en: "Morocco", nl: "Marokko" },
  { id: "788", code: "TN", en: "Tunisia", nl: "Tunesië" },
  { id: "710", code: "ZA", en: "South Africa", nl: "Zuid-Afrika" },
  { id: "682", code: "SA", en: "Saudi Arabia", nl: "Saoedi-Arabië" },
  {
    id: "784",
    code: "AE",
    en: "United Arab Emirates",
    nl: "Verenigde Arabische Emiraten",
  },
  { id: "414", code: "KW", en: "Kuwait", nl: "Koeweit" },
  { id: "368", code: "IQ", en: "Iraq", nl: "Irak" },
  { id: "356", code: "IN", en: "India", nl: "India" },
  { id: "156", code: "CN", en: "China", nl: "China" },
  { id: "764", code: "TH", en: "Thailand", nl: "Thailand" },
  { id: "360", code: "ID", en: "Indonesia", nl: "Indonesië" },
  { id: "036", code: "AU", en: "Australia", nl: "Australië" },
  { id: "554", code: "NZ", en: "New Zealand", nl: "Nieuw-Zeeland" },
  { id: "840", code: "US", en: "United States", nl: "Verenigde Staten" },
  { id: "124", code: "CA", en: "Canada", nl: "Canada" },
  { id: "484", code: "MX", en: "Mexico", nl: "Mexico" },
  { id: "535", code: "BQ", en: "Bonaire", nl: "Bonaire" },
  { id: "534", code: "SX", en: "Sint Maarten", nl: "Sint Maarten" },
  { id: "076", code: "BR", en: "Brazil", nl: "Brazilië" },
  { id: "032", code: "AR", en: "Argentina", nl: "Argentinië" },
]
// Note: France ("250") was added because the user list is European-heavy
// and France visually "links" UK to central Europe — remove the entry if not desired.

// Countries too small to exist in the 110m world data get a dot marker at
// their coordinates instead of a lit-up shape.
const DOT_MARKERS = [
  { id: "535", coordinates: [-68.27, 12.18] }, // Bonaire
  { id: "470", coordinates: [14.38, 35.94] }, // Malta
  { id: "534", coordinates: [-63.06, 18.04] }, // Sint Maarten
]

const PATIENT_COUNTRY_IDS = PATIENT_COUNTRIES.map((c) => c.id)

// A clockwise sweep out from the Netherlands: Europe round to the north-west,
// then North Africa, Russia, the Middle East, Asia, Oceania, southern Africa,
// and the Americas south to north.
// This is deliberately separate from PATIENT_COUNTRIES rather than a reordering
// of it, because the about page renders that same array as a visible list of
// flags and would have been reordered along with the animation.
const CLOCKWISE_CODES = [
  "NL",
  // Europe, clockwise from the Netherlands
  "DE",
  "DK",
  "NO",
  "SE",
  "FI",
  "PL",
  "CZ",
  "SK",
  "HU",
  "RO",
  "RS",
  "MK",
  "AL",
  "GR",
  "MT",
  "IT",
  "AT",
  "CH",
  "ES",
  "PT",
  "FR",
  "LU",
  "BE",
  "GB",
  "IE",
  "IS",
  // North Africa, then Russia and the Middle East
  "MA",
  "TN",
  "RU",
  "TR",
  "IQ",
  "KW",
  "SA",
  "AE",
  // Asia and Oceania
  "IN",
  "CN",
  "TH",
  "ID",
  "AU",
  "NZ",
  // southern Africa, then the Americas south to north
  "ZA",
  "AR",
  "BR",
  "BQ",
  "SX",
  "MX",
  "US",
  "CA",
]
const ID_BY_CODE = new Map(PATIENT_COUNTRIES.map((c) => [c.code, c.id]))
const CLOCKWISE_IDS = CLOCKWISE_CODES.map((c) => ID_BY_CODE.get(c)).filter(
  Boolean
)
const PATIENT_SET = new Set(PATIENT_COUNTRY_IDS)
const ACCENT = "#C8AA78" // gold
const ACCENT_FILL = "rgba(200, 170, 120, 0.55)"
const ACCENT_HOVER = "rgba(200, 170, 120, 0.75)"
const QUIET_STROKE = "rgba(255, 255, 255, 0.2)"

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: "100%",
    maxWidth: "1200px",
    margin: "0 auto",
    [theme.breakpoints.down("sm")]: {
      width: "calc(100% + 40px)",
      maxWidth: "calc(100% + 40px)",
      marginLeft: "-20px",
      marginRight: "-20px",
      transform: "scale(1.15)",
      transformOrigin: "center center",
    },
  },
  map: {
    width: "100%",
    height: "auto",
    display: "block",
  },
}))

// revealInterval: ms between each country lighting up (default 90 — the
//   original pace used on the about page)
// homeHoldMs:     extra pause after the first country (the Netherlands) so it
//   registers alone before the rest follow. 0 = no pause, the old behaviour.
// rampToMs:       if set, the interval eases from revealInterval down to this
//   across the remaining countries, so the fill accelerates. null = constant.
// fadeMs:         how long each country takes to fade in. At a fast interval an
//   800ms fade leaves ~16 countries mid-transition, which blurs the sense of
//   arrivals accumulating; a shorter fade brings that back.
// Defaults reproduce the original behaviour exactly, so the about page is
// unaffected — the new pacing is opt-in per usage.
// revealOrder:    "clockwise" sweeps out from the Netherlands; anything else
//   uses the PATIENT_COUNTRIES order, which is the original behaviour.
const PatientMap = ({
  revealInterval = 90,
  homeHoldMs = 0,
  rampToMs = null,
  fadeMs = 800,
  revealOrder = null,
}) => {
  const revealIds =
    revealOrder === "clockwise" ? CLOCKWISE_IDS : PATIENT_COUNTRY_IDS
  const classes = useStyles()
  const containerRef = useRef(null)
  const [revealedCount, setRevealedCount] = useState(0)

  useEffect(() => {
    if (typeof window === "undefined" || !containerRef.current) return

    // A timeout chain rather than setInterval, because the gap between
    // countries is allowed to change as the reveal progresses.
    let timeoutId
    let cancelled = false
    const total = revealIds.length

    // gap to wait after having revealed country `i` (1-based)
    const gapAfter = (i) => {
      if (i === 1 && homeHoldMs > 0) return homeHoldMs
      if (rampToMs == null || total < 3) return revealInterval
      // ease from revealInterval down to rampToMs across the remainder
      const progress = (i - 1) / (total - 2)
      return revealInterval + (rampToMs - revealInterval) * progress
    }

    const startReveal = () => {
      let i = 0
      setRevealedCount(0)
      const step = () => {
        if (cancelled) return
        i += 1
        setRevealedCount(i)
        if (i < total) timeoutId = setTimeout(step, gapAfter(i))
      }
      step()
    }

    if (typeof IntersectionObserver === "undefined") {
      // Fallback: just reveal immediately
      startReveal()
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry && entry.isIntersecting) {
          startReveal()
          observer.disconnect()
        }
      },
      { threshold: 0.25 }
    )
    observer.observe(containerRef.current)

    return () => {
      cancelled = true
      observer.disconnect()
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [revealInterval, homeHoldMs, rampToMs, revealIds])

  const revealedIds = new Set(revealIds.slice(0, revealedCount))

  return (
    <div ref={containerRef} className={classes.wrapper}>
      <ComposableMap
        projection="geoEqualEarth"
        projectionConfig={{ scale: 210, center: [0, 9] }}
        width={1000}
        height={490}
        style={{ width: "100%", height: "auto" }}
      >
        <Geographies geography={worldData}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const id = String(geo.id)
              const isPatientCountry = PATIENT_SET.has(id)
              const isLit = revealedIds.has(id)
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  style={{
                    default: {
                      fill: isLit ? ACCENT_FILL : "transparent",
                      stroke: isLit ? ACCENT : QUIET_STROKE,
                      strokeWidth: isLit ? 0.7 : 0.5,
                      transition: `fill ${fadeMs}ms ease, stroke ${fadeMs}ms ease, stroke-width ${fadeMs}ms ease`,
                      outline: "none",
                    },
                    hover: {
                      fill: isPatientCountry
                        ? ACCENT_HOVER
                        : "rgba(255, 255, 255, 0.04)",
                      stroke: isPatientCountry
                        ? ACCENT
                        : "rgba(255, 255, 255, 0.18)",
                      strokeWidth: 0.7,
                      outline: "none",
                      cursor: isPatientCountry ? "default" : "default",
                    },
                    pressed: {
                      outline: "none",
                    },
                  }}
                />
              )
            })
          }
        </Geographies>
        {DOT_MARKERS.map((dot) => (
          <Marker key={dot.id} coordinates={dot.coordinates}>
            <circle
              r={2.5}
              fill={ACCENT_FILL}
              stroke={ACCENT}
              strokeWidth={1.2}
              style={{
                opacity: revealedIds.has(dot.id) ? 1 : 0,
                transition: `opacity ${fadeMs}ms ease`,
              }}
            />
          </Marker>
        ))}
      </ComposableMap>
    </div>
  )
}

export default PatientMap
